import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Check, ChevronRight, Plus, Minus, MessageCircle,
  Scale, Cpu, Landmark, PenTool, ShieldPlus, Megaphone, Briefcase, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";
import { submitLead } from "@/lib/api";
import { trackLeadConversion } from "@/lib/analytics";
import {
  YEARS, monthsForYear, getMonthData, getMonthYear, getSampleRows,
  ALL_STATES_UTS, waLink,
} from "@/lib/mcaMonths";

const USE_CASES = [
  { icon: Scale, t: "CA & CS Firms", d: "Newly registered company leads for GST filing, compliance and audit services." },
  { icon: Cpu, t: "Software & SaaS", d: "Sell ERP, CRM and accounting tools to new company leads India wide." },
  { icon: Landmark, t: "Banks & NBFCs", d: "Pitch current accounts and startup credit lines to new entities." },
  { icon: PenTool, t: "Trademark & IP", d: "Reach companies registering brand names in their first 90 days." },
  { icon: ShieldPlus, t: "Insurance", d: "Group health and liability products for new businesses." },
  { icon: Megaphone, t: "Marketing Agencies", d: "Company email database India for targeted B2B campaigns." },
  { icon: Briefcase, t: "Business Consultants", d: "Startup advisory for fresh incorporations, including new business leads Mumbai." },
];

const FAQS = [
  { q: "Where can I buy MCA company data in India?", a: "Datorra India sells verified MCA company data — newly registered companies with CIN, director name, mobile and email. Place an order on WhatsApp at +91 85379 42196 and receive Excel/CSV delivery within 24 hours." },
  { q: "How much does MCA company data cost?", a: "The Trial Pack is ₹3,000 one-time (1-day sample, up to 500 records). The Monthly Plan is ₹20,000 per month with daily delivery and unlimited records. Annual plans are priced on request. All prices are exclusive of GST." },
  { q: "Can I download newly registered company list in Excel?", a: "Yes. Every delivery ships in Excel (.xlsx) and CSV — MCA company data Excel download is our default format, ready to open in any spreadsheet tool or import into your CRM." },
  { q: "What fields are included in the MCA data?", a: "Company name, CIN, registration date, state, ROC, company type (Pvt Ltd, LLP, OPC), director name, mobile, email, paid-up capital where available, registered address and filing status." },
  { q: "How often is the data updated?", a: "Our MCA data is refreshed every 24 hours. The current month shows an 'Updating daily' badge; past months are marked complete once the filing window closes." },
  { q: "Can I buy data for a specific month, for example September 2026?", a: "Yes. Each month has its own page and its own file. Pick the month from the grid above, check the sample and place the order on WhatsApp." },
  { q: "Is it legal to buy MCA company data?", a: "Yes. MCA registration data is publicly available information filed under the Companies Act. Use it for legitimate B2B outreach, honour DND registrations and keep opt-outs current." },
  { q: "Do you provide LLP data separately?", a: "Yes. You can buy LLP database India files filtered by state, month and paid-up capital — LLP list Excel download is available for every month." },
  { q: "What is the minimum order?", a: "The Trial Pack at ₹3,000 one-time is the minimum. Custom one-time bulk files are quoted on request based on filters and volume." },
  { q: "Can I get data for a specific state and month together?", a: "Yes. State and month filters can be combined, along with company type, ROC and paid-up capital band — the dataset is built to your exact specification." },
];

const STEPS = [
  { n: "01", t: "Tell us your target", d: "Share the state, company type and month you want. A short WhatsApp message is enough." },
  { n: "02", t: "Check the sample", d: "We send a sample slice on WhatsApp so you can verify fields and quality before paying." },
  { n: "03", t: "Pay and receive", d: "Confirm the order and get the full Excel/CSV within 24 hours." },
];

const CHECKLIST = [
  { c: "Freshness of data", w: "Stale lists waste calls", d: "Refreshed every 24 hours" },
  { c: "CIN and registration date present", w: "Proves the record is a real incorporation", d: "Included on every row" },
  { c: "Mobile and email coverage", w: "Determines how reachable leads are", d: "Verified owner-level contacts" },
  { c: "State / ROC / type filters", w: "Lets you match the file to your ICP", d: "Full filter set included" },
  { c: "Sample before payment", w: "Validates quality before commitment", d: "Sample sent on WhatsApp" },
  { c: "Delivery time and format", w: "Speed matters for time-sensitive campaigns", d: "Excel / CSV within 24 hours" },
  { c: "Support after purchase", w: "Issues surface during campaigns", d: "WhatsApp support, 7 days" },
];

export default function McaDataHub() {
  const [year, setYear] = useState(YEARS[YEARS.length - 1]);
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: "", phone: "", company: "", month: "", purpose: "Sales" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useSeo({
    title: "Buy MCA Company Data India | Month-wise New Company List Excel | Datorra",
    description:
      "Buy MCA company data online. Month-wise newly registered company lists with CIN, director, email and mobile. Excel/CSV download, price from Rs 20,000/month. Datorra India.",
    path: "/mca-data",
    jsonLd: [
      {
        id: "mcahub-breadcrumb",
        data: {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://datorra.in/" },
            { "@type": "ListItem", position: 2, name: "MCA Data", item: "https://datorra.in/mca-data" },
          ],
        },
      },
      {
        id: "mcahub-product",
        data: {
          "@context": "https://schema.org",
          "@type": "Product",
          name: "MCA Company Data — Month-wise Newly Registered Company Lists",
          description: "Month-wise newly registered company lists from MCA records with CIN, director, mobile and email. Excel/CSV delivery within 24 hours.",
          brand: { "@type": "Brand", name: "Datorra India" },
          areaServed: "IN",
          offers: [
            { "@type": "Offer", name: "Trial Pack", price: "3000", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://datorra.in/mca-data" },
            { "@type": "Offer", name: "Monthly Plan", price: "20000", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://datorra.in/mca-data" },
          ],
        },
      },
      {
        id: "mcahub-faq",
        data: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        },
      },
    ],
  });

  const months = monthsForYear(year);
  const hubWa = waLink("Hi Datorra, I want to buy MCA company data");

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({
        name: form.name,
        email: "not-provided@mca-enquiry.datorra.in",
        phone: form.phone,
        company: form.company,
        requirement: `MCA Data — ${form.month || "Any month"} — ${form.purpose}`,
        message: `Hub enquiry. Month/State: ${form.month || "-"} · Purpose: ${form.purpose}`,
        source: "mca-data-hub",
      });
      trackLeadConversion();
      setDone(true);
      toast.success("Enquiry received. We'll reply on WhatsApp shortly.");
    } catch {
      toast.error("Something went wrong. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="mca-data-hub" className="bg-white">
      {/* 1. HERO */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-20 border-b border-slate-100 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-blue-50/60 blur-3xl rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <nav aria-label="Breadcrumb" className="text-xs md:text-sm text-slate-500 flex items-center gap-1.5">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" strokeWidth={1.75} />
            <span className="text-slate-900 font-medium">MCA Data</span>
          </nav>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-5xl">
            MCA Company Data India: Buy <span className="font-serif-editorial italic">month-wise</span> newly registered company lists
          </h1>

          <p className="mt-6 max-w-3xl text-lg md:text-xl text-slate-600 leading-relaxed">
            Verified month-wise lists of companies registered with the Ministry of Corporate Affairs — CIN, director,
            mobile and email included. Delivered in Excel/CSV within 24 hours.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a href={hubWa} target="_blank" rel="noopener noreferrer" data-testid="hub-hero-sample" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 active:scale-95 transition-all">
              Get Sample Data <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </a>
            <a href="#pricing" data-testid="hub-hero-pricing" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 border border-slate-200 pl-6 pr-5 py-4 text-sm font-medium hover:border-slate-900 active:scale-95 transition-all">
              View Pricing <ChevronRight className="w-4 h-4 rotate-90" strokeWidth={1.75} />
            </a>
          </div>

          {/* G. Trust strip — real, verifiable points only */}
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-600" data-testid="hub-trust-strip">
            {["Daily updated", "Sample before you buy", "Excel / CSV", "WhatsApp support"].map((b) => (
              <span key={b} className="inline-flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-600" strokeWidth={2.25} />
                <span className="font-medium text-slate-800">{b}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 2. MONTH-WISE GRID */}
      <section className="py-20 md:py-24 bg-white" data-testid="hub-month-grid">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ By Month</div>
              <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
                MCA Data by Month: {year}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {YEARS.map((y) => (
                <button
                  key={y}
                  onClick={() => setYear(y)}
                  data-testid={`year-${y}`}
                  className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all ${year === y ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-900"}`}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {months.map((slug) => {
              const { monthName, year: y } = getMonthYear(slug);
              const data = getMonthData(slug);
              const inProgress = data?.status === "in-progress";
              return (
                <div key={slug} data-testid={`month-card-${slug}`} className="relative bg-[#fafafa] border border-slate-100 rounded-3xl p-7 flex flex-col hover:bg-white hover:shadow-md hover:border-blue-100 transition-all">
                  {inProgress && (
                    <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                      Updating daily
                    </span>
                  )}
                  <div className="font-display text-2xl font-medium text-slate-900">{monthName} {y}</div>
                  {typeof data?.total_new_companies === "number" && (
                    <div className="mt-2 text-sm text-slate-600">
                      <span className="font-display text-lg text-slate-900 font-medium">{data.total_new_companies.toLocaleString("en-IN")}</span> {data.total_label || "newly registered companies"}
                    </div>
                  )}
                  {data?.highlight_note && <p className="mt-2 text-sm text-slate-500">{data.highlight_note}</p>}
                  <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-500">
                    <Check className="w-3.5 h-3.5 text-blue-600" strokeWidth={2.25} /> Excel / CSV available
                  </div>
                  <Link to={`/mca-data/${slug}`} data-testid={`month-link-${slug}`} className="mt-6 group inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white pl-5 pr-4 py-3 text-sm font-medium self-start hover:bg-blue-600 transition-colors">
                    View {monthName} {y} Data
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. BUY block */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Buy</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Buy MCA company data online
          </h2>
          <div className="mt-8 space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
            <p>
              When you <strong className="text-slate-900">buy MCA company data</strong> from Datorra, each record carries the company
              name, CIN, registration date, state, ROC, company type (Pvt Ltd, LLP, OPC), director name, mobile and email —
              with paid-up capital where available. It's the most complete way to{" "}
              <strong className="text-slate-900">buy company database India</strong> coverage for fresh incorporations.
            </p>
            <p>
              Whether you want to <strong className="text-slate-900">buy newly registered companies list</strong> files for one month,
              a <strong className="text-slate-900">buy LLP database India</strong> cut by state, or{" "}
              <strong className="text-slate-900">purchase ROC company data</strong> across multiple regions, every delivery ships in
              Excel or CSV within 24 hours. The simplest way to <strong className="text-slate-900">buy MCA data online</strong> is a
              single WhatsApp message — tell us the month and filters, approve the sample, and the file lands in your inbox.
            </p>
          </div>
        </div>
      </section>

      {/* A. 3-step */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Process</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            How to buy MCA company data in 3 steps
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-[#fafafa] border border-slate-100 rounded-3xl p-7">
                <div className="chapter-numeral text-5xl">{s.n}</div>
                <h3 className="mt-4 font-display text-xl font-medium text-slate-900">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <a href={hubWa} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 transition-all">
              Start on WhatsApp <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* B. Buyer checklist */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Checklist</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            What to check before you buy company data
          </h2>
          <div className="mt-12 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                  <th className="p-5 font-medium">What to check</th>
                  <th className="p-5 font-medium">Why it matters</th>
                  <th className="p-5 font-medium bg-blue-50 border-l border-blue-100 text-slate-900">What Datorra provides</th>
                </tr>
              </thead>
              <tbody>
                {CHECKLIST.map((r, i) => (
                  <tr key={r.c} className={`border-t border-slate-100 ${i % 2 === 1 ? "bg-slate-50/40" : "bg-white"}`}>
                    <td className="p-5 font-medium text-slate-800">{r.c}</td>
                    <td className="p-5 text-slate-600">{r.w}</td>
                    <td className="p-5 bg-blue-50/60 border-l border-blue-100">
                      <span className="inline-flex items-center gap-2 text-slate-900 font-medium">
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" strokeWidth={2.25} />
                        {r.d}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 4. Pricing */}
      <section id="pricing" className="py-20 md:py-24 bg-white scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Pricing</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            MCA data <span className="font-serif-editorial italic">price</span>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { t: "Trial Pack", price: "₹3,000", period: "one-time", d: "1-day MCA data sample, up to 500 records. Verify quality before committing.", cta: "Order Trial", featured: false },
              { t: "Monthly Plan", price: "₹20,000", period: "per month", d: "Daily delivery, all states, all business types, unlimited records.", cta: "Order Now", featured: true },
              { t: "Annual Plan", price: "Contact for best price", period: "12 months", d: "Year-long daily delivery, priority support, custom filters, API access.", cta: "Get Quote", featured: false },
            ].map((p) => (
              <div key={p.t} className={`relative rounded-3xl p-8 flex flex-col ${p.featured ? "bg-slate-900 text-white border border-slate-900 shadow-2xl shadow-slate-900/20" : "bg-white border border-slate-100"}`}>
                {p.featured && (
                  <span className="absolute -top-3 left-8 rounded-full bg-blue-600 text-white px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold">Most Popular</span>
                )}
                <h3 className={`font-display text-2xl font-medium ${p.featured ? "text-white" : "text-slate-900"}`}>{p.t}</h3>
                <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                  <span className={`font-display text-3xl font-light ${p.featured ? "text-white" : "text-slate-900"}`}>{p.price}</span>
                  <span className={`text-sm ${p.featured ? "text-slate-300" : "text-slate-500"}`}>{p.period}</span>
                </div>
                <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-slate-300" : "text-slate-600"}`}>{p.d}</p>
                <a href={hubWa} target="_blank" rel="noopener noreferrer" className={`mt-8 group inline-flex items-center gap-2 rounded-full pl-5 pr-4 py-3 text-sm font-medium self-start transition-all ${p.featured ? "bg-white text-slate-900 hover:bg-blue-600 hover:text-white" : "bg-slate-900 text-white hover:bg-blue-600"}`}>
                  {p.cta} <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </a>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-slate-500 text-sm">All prices exclusive of GST.</p>
          <p className="mt-6 max-w-3xl mx-auto text-center text-slate-600 leading-relaxed">
            On <strong className="text-slate-900">company data per record price</strong>: the Monthly Plan is unlimited, so your effective
            cost per record falls as you use more of the file. For one-time bulk files, we quote custom per-record pricing based on
            filters and volume — compare any <strong className="text-slate-900">company database price India</strong> offer against a
            Datorra quote, and check what a transparent <strong className="text-slate-900">new company list price</strong> looks like.
            The <strong className="text-slate-900">MCA data subscription</strong> (₹20,000/month) is the most cost-effective option for
            teams working MCA fresh leads continuously.
          </p>
        </div>
      </section>

      {/* 5. Formats */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Formats</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Download MCA company data in Excel and CSV
          </h2>
          <div className="mt-8 space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
            <p>
              Every delivery is an <strong className="text-slate-900">MCA company data Excel download</strong> — a clean{" "}
              <strong className="text-slate-900">company list with CIN Excel</strong> file, plus a{" "}
              <strong className="text-slate-900">new company data CSV download</strong> for CRM and dialler imports. A{" "}
              <strong className="text-slate-900">state wise company list Excel</strong> split is included when you order
              multiple states, and a dedicated <strong className="text-slate-900">LLP list Excel download</strong> is available
              as a standalone file.
            </p>
            <p>
              Filters available on every order: state, city, ROC, company type (Pvt Ltd / LLP / OPC), incorporation date range
              and paid-up capital band.
            </p>
          </div>
        </div>
      </section>

      {/* 6. Use cases */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Use Cases</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Newly registered company leads for sales teams
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {USE_CASES.map((u) => (
              <div key={u.t} className="bg-[#fafafa] border border-slate-100 rounded-3xl p-6 hover:bg-white hover:shadow-md hover:border-blue-100 transition-all">
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <u.icon className="w-4.5 h-4.5 text-blue-600" strokeWidth={1.5} style={{ width: 18, height: 18 }} />
                </div>
                <h3 className="mt-5 font-display text-lg font-medium text-slate-900">{u.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-slate-600 leading-relaxed">
            As a <strong className="text-slate-900">B2B database India</strong> provider, Datorra pairs these lists with a
            verified <strong className="text-slate-900">company email database India</strong> layer — so sales teams get
            phone and inbox coverage from one file.
          </p>
        </div>
      </section>

      {/* 7. Why Datorra */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Provider</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            MCA data provider India: why Datorra
          </h2>
          <ul className="mt-10 space-y-4">
            {[
              "Refreshed every 24 hours from official filings",
              "Pan India — 28 states and 8 union territories",
              "Sample before you buy, sent on WhatsApp",
              "WhatsApp support, 7 days a week",
              "Custom filters on every order",
              "MCA bulk data and API delivery on request",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-slate-700 text-base md:text-lg">
                <Check className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" strokeWidth={2.25} />
                {b}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-slate-600 leading-relaxed">
            Choosing a <strong className="text-slate-900">company data provider</strong> is mostly about freshness, verification
            and support — the three things Datorra optimizes for.
          </p>
        </div>
      </section>

      {/* 8. Sample table */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Sample</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Sample format
          </h2>
          <div className="mt-10 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                  {["Company Name", "CIN", "State", "Type", "Incorporation Date"].map((h) => (
                    <th key={h} className="p-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getSampleRows("october-2026").map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className={`p-4 font-medium text-slate-900 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.company}</td>
                    <td className={`p-4 font-mono text-xs text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.cin}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.state}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.type}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-slate-400">Sample format. Request real sample on WhatsApp.</p>
        </div>
      </section>

      {/* D. Full coverage grid */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Coverage</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Pan India coverage — every state &amp; union territory
          </h2>
          <div className="mt-10 flex flex-wrap gap-2">
            {ALL_STATES_UTS.map((s) => (
              <a
                key={s}
                href={waLink(`Hi Datorra, I want MCA company data for ${s}`)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2.5 text-sm text-slate-800 hover:border-blue-600 hover:text-blue-700 transition-colors"
              >
                <MapPin className="w-3.5 h-3.5 text-blue-600" strokeWidth={1.75} />
                {s}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* C. Complete guide */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Guide</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Complete guide: how to buy and use MCA company data for B2B sales
          </h2>

          <div className="mt-12 space-y-10 text-base md:text-lg text-slate-700 leading-relaxed">
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">What MCA data contains</h3>
              <p className="mt-4">
                Every record includes the company name, CIN, registration date, state, ROC, company type, director name, mobile
                and email — with paid-up capital where available. The CIN is a permanent identifier, which makes the file easy
                to de-duplicate and enrich against your own CRM.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Pvt Ltd vs LLP vs OPC — who to target</h3>
              <p className="mt-4">
                Pvt Ltd companies are the largest segment (~70% of new registrations) and the best fit for B2B SaaS, banking
                and professional services. LLPs skew toward partnerships, consultancies and agencies — a strong target for
                accounting software and insurance. OPCs are solo founders — small-ticket services convert fastest here.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Why new companies are the best leads in their first 90 days</h3>
              <p className="mt-4">
                A newly incorporated company has open vendor slots for everything: CA, bank account, software, insurance,
                website, marketing. After 90 days most of those decisions are made. That is why month-wise MCA data — worked
                while the paint is still wet — outperforms every other cold source for B2B outreach.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">How to filter by state, ROC and paid-up capital</h3>
              <p className="mt-4">
                Start with geography (state, then city), then company type, then paid-up capital band as a proxy for ambition
                and budget. ROC codes add precision for regional teams. A tight filter beats a large list every time.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Excel vs CSV — and how to import</h3>
              <p className="mt-4">
                Excel (.xlsx) is best for human review and sales team slicing. CSV is the import format for CRMs (Salesforce,
                HubSpot, Zoho) and WhatsApp tools. Both ship with every Datorra order — UTF-8 encoded, de-duplicated, with a
                column dictionary.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Outreach tips — call, email, WhatsApp, LinkedIn</h3>
              <p className="mt-4">
                Reference the incorporation date in your opening line. Sequence channels: WhatsApp day 1, email day 3, call
                day 5, LinkedIn day 7. One well-timed, specific message outperforms five generic ones.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Legal and compliance basics</h3>
              <p className="mt-4">
                MCA registration data is publicly available. Use it for legitimate B2B outreach, honour DND registrations,
                and keep opt-outs current. A data-use agreement accompanies every Datorra delivery.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">How to measure results</h3>
              <p className="mt-4">
                Tag every lead by month, state, type and capital band in your CRM. Track contact rate, meeting rate and close
                rate per segment. After 60 days, double down on the segments that convert and re-negotiate your filters.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Common mistakes</h3>
              <p className="mt-4">
                Buying stale dumps, skipping the sample, over-filtering, single-channel outreach and ignoring DND. Avoid all
                five and a month-wise MCA file becomes the highest-ROI list your team owns.
              </p>
            </div>
            <a href={hubWa} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 transition-all">
              Get Sample Data on WhatsApp <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* F. Lead capture */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Enquire</div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
              Tell us which <span className="font-serif-editorial italic">month</span> you need
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Share your target month, state and purpose. We'll reply with a scoped quote and sample on WhatsApp.
            </p>
          </div>
          <div className="md:col-span-7 bg-white rounded-3xl border border-slate-100 p-8 md:p-10">
            {done ? (
              <div className="flex flex-col items-start" data-testid="hub-enquiry-success">
                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                  <Check className="w-6 h-6 text-blue-600" strokeWidth={1.75} />
                </div>
                <div className="mt-6 font-display text-2xl text-slate-900">We've got it.</div>
                <div className="mt-2 text-slate-600">Our team will reach out shortly — or continue on WhatsApp for a faster reply.</div>
                <a href={hubWa} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-6 pr-5 py-4 text-sm font-medium hover:brightness-95 transition-all">
                  <MessageCircle className="w-4 h-4" strokeWidth={1.75} /> Continue on WhatsApp
                </a>
              </div>
            ) : (
              <form onSubmit={submitEnquiry} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="hub-enquiry-form">
                {[
                  { k: "name", label: "Name*", required: true, type: "text" },
                  { k: "phone", label: "Mobile*", required: true, type: "tel" },
                  { k: "company", label: "Company", required: false, type: "text" },
                  { k: "month", label: "Which month / state", required: false, type: "text" },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">{f.label}</label>
                    <input
                      data-testid={`hub-enquiry-${f.k}`}
                      type={f.type}
                      required={f.required}
                      value={form[f.k]}
                      onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-2.5 text-slate-900 text-base outline-none transition-colors"
                    />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Purpose</label>
                  <select
                    data-testid="hub-enquiry-purpose"
                    value={form.purpose}
                    onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-2.5 text-slate-900 text-base outline-none"
                  >
                    {["Sales", "Marketing", "Banking", "CA firm", "Other"].map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <button data-testid="hub-enquiry-submit" disabled={loading} className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 transition-all disabled:opacity-60">
                    {loading ? "Sending…" : "Request Sample"} <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ FAQ</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Frequently asked questions
          </h2>
          <div className="mt-12 divide-y divide-slate-200">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="py-1">
                  <button data-testid={`hub-faq-toggle-${i}`} onClick={() => setOpenFaq(open ? -1 : i)} aria-expanded={open} className="w-full py-6 flex items-start justify-between gap-6 text-left group">
                    <span className="font-display text-lg md:text-xl font-medium text-slate-900 group-hover:text-blue-600 transition-colors">{f.q}</span>
                    <span className="mt-1 flex-shrink-0 w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center group-hover:border-slate-900 transition-colors">
                      {open ? <Minus className="w-4 h-4" strokeWidth={1.75} /> : <Plus className="w-4 h-4" strokeWidth={1.75} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.2, 0.7, 0.15, 1] }} className="overflow-hidden">
                        <p className="pb-6 pr-16 text-slate-600 leading-relaxed text-base md:text-lg">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 11. CTA band */}
      <section className="relative py-20 md:py-24 bg-slate-900 text-white overflow-hidden">
        <div className="absolute -top-16 -left-16 w-96 h-96 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
            Ready to reach this month's <span className="font-serif-editorial italic">newly registered</span> companies?
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <a href={hubWa} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-7 pr-6 py-5 text-base font-medium hover:brightness-95 active:scale-95 transition-all">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} /> WhatsApp Now
            </a>
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 pl-7 pr-6 py-5 text-base font-medium hover:bg-blue-50 hover:text-blue-700 active:scale-95 transition-all">
              Request Access <ArrowUpRight className="w-5 h-5" strokeWidth={1.75} />
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-2 text-xs">
            <Link to="/mca-company-database" className="rounded-full border border-white/20 px-4 py-2 text-white/80 hover:border-white hover:text-white transition-colors">MCA Company Database</Link>
            <Link to="/gst-database" className="rounded-full border border-white/20 px-4 py-2 text-white/80 hover:border-white hover:text-white transition-colors">GST Database</Link>
            <Link to="/gst-sales-purchase-data" className="rounded-full border border-white/20 px-4 py-2 text-white/80 hover:border-white hover:text-white transition-colors">GST Sales & Purchase</Link>
            <Link to="/contact" className="rounded-full border border-white/20 px-4 py-2 text-white/80 hover:border-white hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </section>

      <div className="bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 text-xs text-slate-400">
          MCA registration data is publicly available. Use it for legitimate B2B outreach, honour DND registrations and keep opt-outs current.
        </div>
      </div>
    </div>
  );
}
